import { motion } from "framer-motion";
import { useState, type JSX } from "react";

import { fadeRise } from "../lib/motion";
import { type MissionActionName, type UiMissionStep, type UiMissionView } from "../lib/types";
import { useVoss } from "../state/VossProvider";
import { Badge, Button } from "./primitives";

const STATUS_TONE: Record<UiMissionView["status"], "good" | "warn" | "neutral" | "accent"> = {
  planned: "neutral",
  running: "accent",
  paused: "warn",
  "awaiting-approval": "warn",
  completed: "good",
  failed: "warn",
  cancelled: "neutral",
};

/** Human, honest step state. For a dry-run mission a completed step is "simulated", never "executed". */
function stepLabel(step: UiMissionStep, dryRun: boolean): string {
  switch (step.status) {
    case "completed":
      return dryRun ? "simulated ✓" : "done ✓";
    case "awaiting-approval":
      return "awaiting approval";
    case "running":
      return dryRun ? "running (dry run)" : "running";
    case "failed":
      return "failed";
    case "rolled-back":
      return "rolled back";
    case "skipped":
      return "skipped";
    default:
      return "pending";
  }
}

const ACTION_LABEL: Record<MissionActionName, string> = {
  approve: "Approve",
  reject: "Reject",
  pause: "Pause",
  resume: "Resume",
  cancel: "Cancel",
  retry: "Retry",
  rollback: "Restore checkpoint",
  "request-rollback": "Roll back real effects",
  "approve-rollback": "Approve rollback",
  "reject-rollback": "Reject rollback",
};

/** Human, honest recovery phrasing for the recovery banner. */
const RECOVERY_HEADLINE: Record<string, string> = {
  requested: "Rollback requested",
  "awaiting-approval": "Awaiting rollback approval",
  "rolling-back": "Rolling back real effects…",
  "rolled-back": "Rolled back — real effects undone",
  "rollback-failed": "Rollback failed — recovery incomplete",
  "manual-required": "Manual recovery required",
};

const RECOVERY_TONE: Record<string, "good" | "warn" | "neutral" | "accent"> = {
  requested: "accent",
  "awaiting-approval": "warn",
  "rolling-back": "accent",
  "rolled-back": "good",
  "rollback-failed": "warn",
  "manual-required": "warn",
};

export function MissionCard({ missionId }: { missionId: string }): JSX.Element | null {
  const { missions, runMissionAction } = useVoss();
  const [busy, setBusy] = useState(false);
  const view = missions[missionId];
  if (!view) return null;

  const pct = view.progress.total > 0 ? Math.round((view.progress.completed / view.progress.total) * 100) : 0;
  const firstFailedStep = view.goals.flatMap((g) => g.tasks.flatMap((t) => t.steps)).find((s) => s.status === "failed");
  const latestCheckpoint = view.checkpoints.length > 0 ? view.checkpoints[view.checkpoints.length - 1] : null;

  const awaitingRollback = view.recovery?.awaiting ?? null;

  const run = (action: MissionActionName): void => {
    let payload: { stepId?: string; checkpointId?: string; attemptId?: string; reason?: string } | undefined;
    if (action === "approve" && view.pendingApproval) payload = { stepId: view.pendingApproval.stepId };
    else if (action === "reject" && view.pendingApproval) payload = { stepId: view.pendingApproval.stepId, reason: "Rejected by operator" };
    else if (action === "retry" && firstFailedStep) payload = { stepId: firstFailedStep.id };
    else if (action === "rollback" && latestCheckpoint) payload = { checkpointId: latestCheckpoint.id };
    else if (action === "approve-rollback" && awaitingRollback) payload = { attemptId: awaitingRollback.attemptId };
    else if (action === "reject-rollback" && awaitingRollback) payload = { attemptId: awaitingRollback.attemptId, reason: "Rollback rejected by operator" };
    setBusy(true);
    void runMissionAction(missionId, action, payload).finally(() => setBusy(false));
  };

  // Approve/Reject (step + rollback) render in their own banners; the rest come from availableActions.
  const IN_BANNER = new Set<MissionActionName>(["approve", "reject", "approve-rollback", "reject-rollback"]);
  const secondaryActions = view.availableActions.filter((a) => !IN_BANNER.has(a));
  const recovery = view.recovery;

  return (
    <motion.section className="voss-mission" variants={fadeRise} initial="hidden" animate="visible" aria-label="Mission" style={{ marginTop: 10, border: "1px solid var(--voss-border, rgba(255,255,255,0.12))", borderRadius: 14, padding: 14 }}>
      <header style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ flex: 1, minWidth: 160 }}>{view.title}</strong>
        <Badge tone={STATUS_TONE[view.status]} dot>
          {view.phase}
        </Badge>
        {view.dryRun ? <Badge tone="accent">DRY RUN — simulated</Badge> : <Badge tone="warn">LIVE — real effects</Badge>}
      </header>
      <p style={{ margin: "6px 0 4px", opacity: 0.7, fontSize: 13 }}>
        {view.dryRun
          ? `No real action is performed — every step is a dry run. ${view.progress.completed}/${view.progress.total} steps simulated.`
          : `This mission performs real, approved effects. ${view.progress.completed}/${view.progress.total} steps complete. Each side-effecting step still asks for your approval first.`}
      </p>
      <div aria-label="Progress" style={{ height: 6, borderRadius: 4, background: "rgba(255,255,255,0.1)", overflow: "hidden", margin: "6px 0 12px" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: "var(--voss-accent, #7c9cff)" }} />
      </div>

      {view.approvalRequired && view.pendingApproval ? (
        <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,190,80,0.08)", marginBottom: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            <strong>Approval required</strong> — a <code>{view.pendingApproval.kind}</code> step is paused: {view.pendingApproval.summary}
          </div>
          {view.pendingApproval.preview ? (
            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 8, display: "grid", gap: 2 }}>
              <div>
                <strong>What will happen:</strong> {view.pendingApproval.preview.sideEffectClass} on{" "}
                <code>{view.pendingApproval.preview.target ?? "—"}</code>
              </div>
              <div>
                <strong>Why approval:</strong> {view.pendingApproval.preview.reason} <span style={{ opacity: 0.6 }}>(sandbox: {view.pendingApproval.preview.profile})</span>
              </div>
              <div>
                <strong>Reversibility:</strong> {view.pendingApproval.preview.reversibility}
                {view.pendingApproval.preview.rollbackStrategy !== "none" ? ` (${view.pendingApproval.preview.rollbackStrategy})` : ""}
                {view.pendingApproval.preview.dryRun ? " · simulated (dry run)" : ""}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
              <em>Simulated — nothing runs without your approval.</em>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="primary" disabled={busy} onClick={() => run("approve")}>
              Approve
            </Button>
            <Button disabled={busy} onClick={() => run("reject")}>
              Reject
            </Button>
          </div>
        </div>
      ) : null}

      {recovery && recovery.status !== "none" ? (
        <div style={{ padding: 10, borderRadius: 10, background: "rgba(120,156,255,0.08)", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Badge tone={RECOVERY_TONE[recovery.status] ?? "neutral"} dot>
              {RECOVERY_HEADLINE[recovery.status] ?? recovery.status}
            </Badge>
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 3, fontSize: 12, opacity: 0.9 }}>
            {recovery.effects.map((e) => (
              <li key={e.attemptId} style={{ display: "flex", gap: 8 }}>
                <span style={{ flex: 1 }}>
                  <code>{e.capability}</code> {e.target ? <span style={{ opacity: 0.7 }}>· {e.target}</span> : null}{" "}
                  <span style={{ opacity: 0.55 }}>({e.strategy}, {e.klass})</span>
                </span>
                <span style={{ opacity: 0.75 }}>{e.state}</span>
              </li>
            ))}
          </ul>
          {awaitingRollback ? (
            <>
              <p style={{ fontSize: 12, opacity: 0.85, margin: "8px 0" }}>
                Undo <code>{awaitingRollback.capability}</code> on <code>{awaitingRollback.target ?? "—"}</code> via{" "}
                {awaitingRollback.strategy}. This performs a real recovery and is authorized server-side.
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="primary" disabled={busy} onClick={() => run("approve-rollback")}>
                  Approve rollback
                </Button>
                <Button disabled={busy} onClick={() => run("reject-rollback")}>
                  Reject rollback
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
        {view.goals.flatMap((goal) =>
          goal.tasks.map((task) => (
            <li key={task.id} style={{ fontSize: 13 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ flex: 1 }}>
                  <strong>{task.title}</strong> <span style={{ opacity: 0.55 }}>· {task.capability}</span>
                </span>
                <Badge tone={task.status === "completed" ? "good" : task.status === "failed" ? "warn" : "neutral"}>{task.status === "completed" ? (view.dryRun ? "simulated" : "done") : task.status}</Badge>
              </div>
              <ul style={{ listStyle: "none", margin: "4px 0 0", padding: "0 0 0 12px", display: "grid", gap: 2, opacity: 0.85 }}>
                {task.steps.map((step) => (
                  <li key={step.id} style={{ display: "flex", gap: 8 }}>
                    <span style={{ flex: 1 }}>
                      {step.title} <span style={{ opacity: 0.5 }}>({step.kind})</span>
                    </span>
                    <span style={{ opacity: 0.7 }}>{stepLabel(step, view.dryRun)}</span>
                  </li>
                ))}
              </ul>
            </li>
          )),
        )}
      </ol>

      {view.evidence.length > 0 ? (
        <details style={{ marginTop: 10, fontSize: 12, opacity: 0.85 }}>
          <summary>Evidence ({view.evidence.length}) · checkpoints ({view.checkpoints.length})</summary>
          <ul style={{ margin: "6px 0 0", paddingLeft: 16 }}>
            {view.evidence.map((e) => (
              <li key={e.id}>
                <span style={{ opacity: 0.6 }}>[{e.kind}]</span> {e.summary} <code style={{ opacity: 0.5 }}>{e.hash.slice(0, 8)}</code>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {view.availableActions.includes("request-rollback") && (!recovery || recovery.status === "none") ? (
        <p style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
          <strong>Recovery available</strong> — real effects from this mission can be undone. Rolling back re-authorizes each undo server-side.
        </p>
      ) : null}

      {secondaryActions.length > 0 ? (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {secondaryActions.map((a) => (
            <Button key={a} disabled={busy} onClick={() => run(a)}>
              {ACTION_LABEL[a]}
            </Button>
          ))}
        </div>
      ) : null}
    </motion.section>
  );
}
